/**
 * Focus management utilities for handling focus restoration
 * after page refreshes or context changes
 */

export interface FocusContext {
  id?: string;
  dataFocusId?: string;
  selector?: string;
  className?: string;
  action?: string; // Added to track the context of focus save (e.g., 'budget-file-switch')
}

export const focusManagement = {
  /**
   * Save the current focus context
   */
  saveFocus: (): FocusContext => {
    const active = document.activeElement as HTMLElement;
    if (!active) return {};
    
    return {
      id: active.id,
      dataFocusId: active.getAttribute('data-focus-id') || undefined,
      selector: active.tagName.toLowerCase(),
      className: active.className,
    };
  },
  
  /**
   * Restore focus based on saved context
   */
  restoreFocus: (savedFocus: FocusContext) => {
    if (!savedFocus) return;
    
    let element: HTMLElement | null = null;
    
    // Try to find element by ID first
    if (savedFocus.id) {
      element = document.getElementById(savedFocus.id);
    }
    
    // Try data-focus-id attribute
    if (!element && savedFocus.dataFocusId) {
      element = document.querySelector(`[data-focus-id="${savedFocus.dataFocusId}"]`);
    }
    
    // Fall back to main heading
    if (!element) {
      element = document.querySelector('main h1, [role="main"] h1, main h2');
    }
    
    // Focus the element if found
    if (element) {
      // Ensure element is focusable
      if (element.tabIndex === -1) {
        element.tabIndex = -1;
      }
      element.focus();
      
      // Announce focus restoration to screen readers
      const message = `Focus restored to ${element.getAttribute('aria-label') || element.innerText || 'main content'}`;
      const announcement = document.createElement('div');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('aria-live', 'polite');
      announcement.className = 'sr-only';
      announcement.textContent = message;
      document.body.appendChild(announcement);
      
      // Remove announcement after delay
      setTimeout(() => {
        document.body.removeChild(announcement);
      }, 1000);
    }
  },
  
  /**
   * Store focus context in session storage
   */
  storeFocusContext: (context: FocusContext) => {
    sessionStorage.setItem('budgetSwitchFocus', JSON.stringify(context));
  },
  
  /**
   * Retrieve and clear focus context from session storage
   */
  retrieveFocusContext: (): FocusContext | null => {
    const stored = sessionStorage.getItem('budgetSwitchFocus');
    if (stored) {
      sessionStorage.removeItem('budgetSwitchFocus');
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  },
};